/**
 * DEACTIVATION REVIEW DETAIL PAGE
 *
 * Platform Team: View deactivation request details and approve/disapprove
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import { createClient } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Building2,
  MapPin,
  FileText,
  XCircle,
  CreditCard,
} from 'lucide-react';
import { DeactivationReviewActions } from '@/components/deactivation-review-actions';
import type { LocationDeactivationQueue } from '@/types/database';

interface DeactivationWithOrg extends LocationDeactivationQueue {
  organization?: {
    id: string;
    name: string;
    logo_url: string | null;
  };
}

function DeactivationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [deactivation, setDeactivation] = useState<DeactivationWithOrg | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Auth check
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-gray-100 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user || (user.role !== 'PLATFORM_TEAM' && user.role !== 'PLATFORM_ADMIN' && user.role !== 'SYSTEM_ADMIN')) {
    navigate('/unauthorized');
    return null;
  }

  // Data fetching
  useEffect(() => {
    const fetchDeactivation = async () => {
      if (!id) {
        navigate('/review');
        return;
      }

      try {
        setLoading(true);
        const supabase = createClient();

        // Get deactivation request with organization details
        const { data: deactivationData, error: err } = await supabase
          .from('location_deactivation_queue')
          .select(`
            *,
            organization:organizations(id, name, logo_url)
          `)
          .eq('id', id)
          .single();

        if (err || !deactivationData) {
          console.error('Error fetching deactivation:', err);
          navigate('/review');
          return;
        }

        setDeactivation(deactivationData as DeactivationWithOrg);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Unknown error'));
        console.error('Fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDeactivation();
  }, [id, navigate]);

  // Render states
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-gray-100 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading deactivation request...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400">Error loading deactivation request</p>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">{error.message}</p>
        </div>
      </div>
    );
  }

  if (!deactivation) {
    return null;
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PENDING_APPROVAL':
        return 'Pending Approval';
      case 'APPROVED':
        return 'Approved';
      case 'DISAPPROVED':
        return 'Disapproved';
      case 'AWAITING_IT_DECONFIG':
        return 'Awaiting IT Deconfiguration';
      case 'AWAITING_DATA_DECONFIG':
        return 'Awaiting Data Deconfiguration';
      case 'AWAITING_DEACTIVATION':
        return 'Awaiting Deactivation';
      case 'DEACTIVATED':
        return 'Deactivated';
      default:
        return status;
    }
  };

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-3'>
          <div className='w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/20 flex items-center justify-center'>
            <XCircle className='w-6 h-6 text-red-600 dark:text-red-400' />
          </div>
          <div>
            <h1 className='text-3xl font-bold text-gray-900 dark:text-gray-100'>
              Deactivation Request
            </h1>
            <p className='text-gray-600 dark:text-gray-400 mt-1'>
              {deactivation.location_name}
            </p>
          </div>
        </div>
        <div className='px-3 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-full text-sm font-medium'>
          {getStatusLabel(deactivation.status)}
        </div>
      </div>

      {/* Disclaimer */}
      {deactivation.status === 'PENDING_APPROVAL' && (
        <Card className='border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/20'>
          <CardContent className='pt-2'>
            <p className='text-sm text-gray-900 dark:text-gray-100'>
              <strong>Important:</strong> After this deactivation request is approved, the subscription will end on{' '}
              <strong>
                {new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </strong>{' '}
              (30 days from now). IT and Data team deconfiguration tasks will only be created after the subscription ends.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Location Details */}
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
        {/* Location Information */}
        <Card>
          <CardHeader>
            <CardTitle className='flex items-center gap-2'>
              <MapPin className='w-5 h-5' />
              Location Information
            </CardTitle>
          </CardHeader>
          <CardContent className='space-y-4'>
            <div>
              <label className='text-sm font-medium text-gray-600 dark:text-gray-400'>
                Location Name
              </label>
              <p className='text-gray-900 dark:text-gray-100'>
                {deactivation.location_name}
              </p>
            </div>

            {deactivation.location_description && (
              <div>
                <label className='text-sm font-medium text-gray-600 dark:text-gray-400'>
                  Description
                </label>
                <p className='text-gray-900 dark:text-gray-100'>
                  {deactivation.location_description}
                </p>
              </div>
            )}

            {deactivation.location_logo && (
              <div>
                <label className='text-sm font-medium text-gray-600 dark:text-gray-400'>
                  Logo
                </label>
                <div className='mt-2 w-24 h-24 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800'>
                  <img
                    src={deactivation.location_logo}
                    alt={deactivation.location_name}
                    className='w-full h-full object-cover'
                  />
                </div>
              </div>
            )}

            <div>
              <label className='text-sm font-medium text-gray-600 dark:text-gray-400'>
                Requested
              </label>
              <p className='text-gray-900 dark:text-gray-100'>
                {formatDate(deactivation.requested_at)}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Organization Information */}
        <Card>
          <CardHeader>
            <CardTitle className='flex items-center gap-2'>
              <Building2 className='w-5 h-5' />
              Organization Information
            </CardTitle>
          </CardHeader>
          <CardContent className='space-y-4'>
            {deactivation.organization && (
              <>
                <div>
                  <label className='text-sm font-medium text-gray-600 dark:text-gray-400'>
                    Organization Name
                  </label>
                  <p className='text-gray-900 dark:text-gray-100'>
                    {deactivation.organization.name}
                  </p>
                </div>

                {deactivation.organization.logo_url && (
                  <div>
                    <label className='text-sm font-medium text-gray-600 dark:text-gray-400'>
                      Logo
                    </label>
                    <div className='mt-2 w-24 h-24 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800'>
                      <img
                        src={deactivation.organization.logo_url}
                        alt={deactivation.organization.name}
                        className='w-full h-full object-cover'
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Subscription Information */}
      {deactivation.stripe_subscription_id && (
        <Card>
          <CardHeader>
            <CardTitle className='flex items-center gap-2'>
              <CreditCard className='w-5 h-5' />
              Subscription Information
            </CardTitle>
          </CardHeader>
          <CardContent className='space-y-4'>
            <div>
              <label className='text-sm font-medium text-gray-600 dark:text-gray-400'>
                Stripe Subscription ID
              </label>
              <p className='text-gray-900 dark:text-gray-100 font-mono text-sm'>
                {deactivation.stripe_subscription_id}
              </p>
            </div>
            <p className='text-sm text-gray-600 dark:text-gray-400'>
              Subscription will be cancelled at the end of the current billing period after all deconfiguration tasks are completed.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Review Actions */}
      {deactivation.status === 'PENDING_APPROVAL' && user && (
        <DeactivationReviewActions
          deactivationRequest={deactivation}
          userId={user.id}
        />
      )}

      {/* Review History (if reviewed) */}
      {(deactivation.approved_at || deactivation.disapproved_at) && (
        <Card>
          <CardHeader>
            <CardTitle className='flex items-center gap-2'>
              <FileText className='w-5 h-5' />
              Review History
            </CardTitle>
          </CardHeader>
          <CardContent className='space-y-4'>
            {deactivation.approved_at && (
              <>
                <div>
                  <label className='text-sm font-medium text-gray-600 dark:text-gray-400'>
                    Approved At
                  </label>
                  <p className='text-gray-900 dark:text-gray-100'>
                    {formatDate(deactivation.approved_at)}
                  </p>
                </div>

                {deactivation.review_notes && (
                  <div>
                    <label className='text-sm font-medium text-gray-600 dark:text-gray-400'>
                      Review Notes
                    </label>
                    <p className='text-gray-900 dark:text-gray-100'>
                      {deactivation.review_notes}
                    </p>
                  </div>
                )}
              </>
            )}

            {deactivation.disapproved_at && (
              <>
                <div>
                  <label className='text-sm font-medium text-gray-600 dark:text-gray-400'>
                    Disapproved At
                  </label>
                  <p className='text-gray-900 dark:text-gray-100'>
                    {formatDate(deactivation.disapproved_at)}
                  </p>
                </div>

                {deactivation.disapproval_reason && (
                  <div>
                    <label className='text-sm font-medium text-gray-600 dark:text-gray-400'>
                      Disapproval Reason
                    </label>
                    <p className='text-red-600 dark:text-red-400'>
                      {deactivation.disapproval_reason}
                    </p>
                  </div>
                )}
              </>
            )}

            {deactivation.deactivated_at && (
              <div>
                <label className='text-sm font-medium text-gray-600 dark:text-gray-400'>
                  Deactivated At
                </label>
                <p className='text-gray-900 dark:text-gray-100'>
                  {formatDate(deactivation.deactivated_at)}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default DeactivationDetailPage;
